import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Grid, Card, CardMedia, CardContent, Typography, Button, Container, 
  Tabs, Tab, Box, TextField, Select, MenuItem, FormControl, InputLabel,
  Slider, InputAdornment, IconButton, Rating
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { setVehicles, setLoading } from '../store/slices/vehicleSlice';
import { db } from '../config/firebase.js';
import { collection, getDocs } from 'firebase/firestore';
import VehicleDetailsModal from '../components/VehicleDetailsModal';

function VehicleListing() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { vehicles, loading } = useSelector(state => state.vehicles);
  
  // Filters and sorting state
  const [vehicleType, setVehicleType] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Get unique brands from vehicles
  const brands = ['all', ...new Set(vehicles.map(vehicle => vehicle.brand))];

  useEffect(() => {
    const fetchVehicles = async () => {
      dispatch(setLoading(true));
      try {
        console.log('Fetching vehicles...');
        const vehiclesCollection = collection(db, 'vehicles');
        const vehiclesSnapshot = await getDocs(vehiclesCollection);
        const vehiclesList = vehiclesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Fetched vehicles:', vehiclesList);
        dispatch(setVehicles(vehiclesList));
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchVehicles();
  }, [dispatch]);

  // Filter vehicles based on all criteria
  const filteredVehicles = vehicles
    .filter(vehicle => 
      // Type filter
      (vehicleType === 'all' ? true : vehicle.type === vehicleType) &&
      // Price range filter
      vehicle.rentPerHour >= priceRange[0] && vehicle.rentPerHour <= priceRange[1] &&
      // Brand filter
      (selectedBrand === 'all' ? true : vehicle.brand === selectedBrand) &&
      // Search query
      (vehicle.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
       vehicle.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
       vehicle.brand.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort((a, b) => {
      // Sorting logic
      const sortValue = sortOrder === 'asc' ? 1 : -1;
      switch (sortBy) {
        case 'price':
          return (a.rentPerHour - b.rentPerHour) * sortValue;
        case 'name':
          return a.name.localeCompare(b.name) * sortValue;
        case 'brand':
          return a.brand.localeCompare(b.brand) * sortValue;
        default:
          return 0;
      }
    });

  if (loading) return <div>Loading...</div>;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Search Bar */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search vehicles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* Vehicle Type Tabs */}
        <Tabs 
          value={vehicleType} 
          onChange={(e, newValue) => setVehicleType(newValue)}
        >
          <Tab label="All Vehicles" value="all" />
          <Tab label="Cars" value="car" />
          <Tab label="Bikes" value="bike" />
          <Tab label="Luxury" value="luxury" />
          <Tab label="Electric" value="electric" />
        </Tabs>

        {/* Brand Filter */}
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Brand</InputLabel>
          <Select
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
            label="Brand"
          >
            {brands.map(brand => (
              <MenuItem key={brand} value={brand}>
                {brand === 'all' ? 'All Brands' : brand}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sort Options */}
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            label="Sort By"
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="price">Price</MenuItem>
            <MenuItem value="brand">Brand</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Order</InputLabel>
          <Select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            label="Order"
          >
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Price Range Slider */}
      <Box sx={{ mb: 4, px: 2 }}>
        <Typography gutterBottom>Price Range (₹/hour)</Typography>
        <Slider
          value={priceRange}
          onChange={(e, newValue) => setPriceRange(newValue)}
          valueLabelDisplay="auto"
          min={0}
          max={1000}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>₹{priceRange[0]}</Typography>
          <Typography>₹{priceRange[1]}</Typography>
        </Box>
      </Box>

      {/* Vehicle Grid */}
      <Grid container spacing={4}>
        {filteredVehicles.map(vehicle => (
          <Grid item key={vehicle.id} xs={12} sm={6} md={4}>
            <Card>
              <CardMedia
                component="img"
                height="200"
                image={vehicle.image}
                alt={vehicle.name}
                sx={{ cursor: 'pointer' }}
                onClick={() => setSelectedVehicle(vehicle)}
              />
              <CardContent>
                <Typography gutterBottom variant="h5" component="h2">
                  {vehicle.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Brand: {vehicle.brand}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Model: {vehicle.model}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Type: {vehicle.type.charAt(0).toUpperCase() + vehicle.type.slice(1)}
                </Typography>
                <Typography variant="h6" color="primary">
                  ₹{vehicle.rentPerHour}/hour
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <Rating value={vehicle.ratings.average} readOnly size="small" precision={0.5} />
                  <Typography variant="body2">({vehicle.ratings.count})</Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                  sx={{
                    mt: 2,
                    backgroundColor: '#800020',
                    '&:hover': {
                      backgroundColor: 'rgba(128, 0, 32, 0.7)',
                      transform: 'translateY(-1px)',
                      transition: 'all 0.3s ease',
                      backdropFilter: 'blur(4px)',
                    }
                  }}
                >
                  View Details
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <VehicleDetailsModal
        vehicle={selectedVehicle}
        open={!!selectedVehicle}
        onClose={() => setSelectedVehicle(null)}
      />
    </Container>
  );
}

export default VehicleListing; 